import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  UsersRound,
  LayoutGrid,
  FileText,
  Package,
  History,
  Newspaper,
  ChartColumnBig,
  Bot,
  Shield,
  Settings,
  UserCheck,
  BrickWallShield,
  Trash2,
} from "lucide-react";
import "../styles/components/sideBar.css";
import { ReactComponent as LogoSvg } from "../assets/icons/Logo.svg";
import { ReactComponent as LogoSmallSvg } from "../assets/icons/mini-logo.svg";
import { useAuth, UserRole } from "../context/AuthContext";

interface MenuItem {
  path: string;
  icon: React.ComponentType<any>;
  label: string;
  description?: string;
}

const menuItems: MenuItem[] = [
  {
    path: "/dashboard",
    icon: LayoutGrid,
    label: "Tổng quan",
    description: "Dashboard cá nhân",
  },
  {
    path: "/posts/my-posts",
    icon: FileText,
    label: "Bài viết của tôi",
    description: "Quản lý bài viết cá nhân",
  },
  {
    path: "/service",
    icon: Package,
    label: "Gói dịch vụ",
    description: "Quản lý gói dịch vụ",
  },
  // Moderator
  {
    path: "/moderator/DashboardModerator",
    icon: LayoutGrid,
    label: "Tổng quan",
    description: "Dashboard quản lý kiểm duyệt",
  },
  {
    path: "/moderator/pending-posts",
    icon: Newspaper,
    label: "Kiểm duyệt bài viết",
    description: "Duyệt bài viết chờ xử lý",
  },
  // Admin
  {
    path: "/admin/dashboard",
    icon: LayoutGrid,
    label: "Tổng quan Admin",
    description: "Dashboard quản trị",
  },
  {
    path: "/admin/users",
    icon: UsersRound,
    label: "Quản lý người dùng",
    description: "Quản lý tài khoản người dùng",
  },
  {
    path: "/admin/statistics",
    icon: ChartColumnBig,
    label: "Thống kê hệ thống",
    description: "Thống kê và báo cáo",
  },
  {
    path: "/admin/transactions",
    icon: History,
    label: "Quản lý giao dịch",
    description: "Quản lý tất cả giao dịch",
  },
  {
    path: "/admin/categories",
    icon: Settings,
    label: "Quản lý danh mục",
    description: "Quản lý danh mục bài viết",
  },
  {
    path: "/admin/moderation/terms",
    icon: Bot,
    label: "Bot kiểm duyệt",
    description: "Quản lý danh sách từ nhạy cảm",
  },
  {
    path: "/admin/terms",
    icon: Shield,
    label: "Quản lý điều khoản",
    description: "Quản lý điều khoản sử dụng",
  },
  {
    path: "/admin/privacy-policy",
    icon: BrickWallShield,
    label: "Quản lý chính sách",
    description: "Quản lý chính sách bảo mật",
  },
  {
    path: "/admin/logs",
    icon: UserCheck,
    label: "Nhật ký hoạt động",
    description: "Xem nhật ký hoạt động",
  },
  {
    path: "/admin/trash",
    icon: Trash2,
    label: "Thùng rác",
    description: "Quản lý dữ liệu đã xóa",
  },
];

const Sidebar: React.FC = () => {
  const [collapsed, setCollapsed] = useState(true);
  const location = useLocation();
  const { user } = useAuth();

  const getFilteredMenuItems = (): MenuItem[] => {
    if (!user) return [];

    const { role } = user;

    // Người dùng thường
    if (
      role === UserRole.MEMBER ||
      role === UserRole.PREMIUM ||
      role === UserRole.TOOL_USER
    ) {
      return menuItems.filter((item: MenuItem) =>
        ["/dashboard", "/posts/my-posts", "/service", "/transactions"].includes(
          item.path
        )
      );
    }

    // Moderator
    if (role === UserRole.MODERATOR) {
      return menuItems.filter((item: MenuItem) =>
        ["/dashboard", "/posts/my-posts", "/service"].includes(item.path) ||
        item.path.startsWith("/moderator") ||
        ["/admin/statistics", "/admin/terms", "/admin/privacy-policy", "/admin/transactions"].includes(
          item.path
        )
      );
    }

    // Admin
    if (role === UserRole.ADMIN) {
      return menuItems;
    }

    return [];
  };

  const groupedItems = {
    personal: getFilteredMenuItems().filter((item: MenuItem) =>
      ["/dashboard", "/posts/my-posts", "/service"].includes(item.path)
    ),
    moderation: getFilteredMenuItems().filter((item: MenuItem) =>
      item.path.startsWith("/moderator")
    ),
    admin: getFilteredMenuItems().filter((item: MenuItem) =>
      item.path.startsWith("/admin")
    ),
  };

  // ✅ Đã fix phần click toàn vùng item
  const renderMenuGroup = (items: MenuItem[], title?: string) => {
    if (items.length === 0) return null;

    return (
      <div className="menu-group">
        {title && (
          <div className="menu-group-title" title={title}>
            {!collapsed ? title : ""}
          </div>
        )}
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-item${isActive ? " active" : ""}`}
              title={collapsed ? item.label : undefined}
            >
              <div className="sidebar-item-content">
                <Icon size={22} />
                {!collapsed && <span>{item.label}</span>}
              </div>
            </Link>
          );
        })}
      </div>
    );
  };

  return (
    <aside
      className={`sidebar${collapsed ? " collapsed" : ""}`}
      onMouseEnter={() => setCollapsed(false)}
      onMouseLeave={() => setCollapsed(true)}
    >
      <div className="sidebar-logo">
        {collapsed ? (
          <LogoSmallSvg className="sidebar-logo-mini" />
        ) : (
          <LogoSvg className="sidebar-logo-img" />
        )}
      </div>

      <nav className="sidebar-nav">
        {renderMenuGroup(groupedItems.personal, "Cá nhân")}
        {renderMenuGroup(groupedItems.moderation, "Kiểm duyệt")}
        {renderMenuGroup(groupedItems.admin, "Quản trị")}
      </nav>
    </aside>
  );
};

export default Sidebar;
